import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Breadcrumb, Button, Col, Container, Row } from 'react-bootstrap';
import { LuChevronLeft, LuChevronRight, LuPlay } from 'react-icons/lu';

import GradientCTA from '../app/components/GradientCTA';
import PodcastLibrarySection from '../app/components/PodcastLibrarySection';
import PodcastShowEpisodesSection from '../app/components/PodcastShowEpisodesSection';
import HeroAnimated from '../app/components/HeroAnimated';
import ResourcesHeroWaves from '../app/components/ResourcesHeroWaves';
import EpisodeThumbnail from '../app/components/EpisodeThumbnail';
import ResourcesPodcastPlayer from '../app/components/ResourcesPodcastPlayer';
import RevealSection from '../app/components/RevealSection';
import SbrIcon from '../app/components/SbrIcon';
import BlogPostCard from '../app/components/BlogPostCard';
import {
  fetchLatestSbrBlogPosts,
  SBR_BLOG_INDEX_PATH,
  type SbrBlogPostSummary,
} from '../lib/sbrBlog';
import { fetchLatestYouTubeEpisodes, YOUTUBE_CHANNEL_VIDEOS_URL, type YouTubeEpisode } from '../lib/youtube';
import type { PodcastShow } from '../data/podcastShows';

type ResourceSection = 'podcast' | 'blog';

function sectionFromHash(hash: string): ResourceSection {
  return hash === '#blog' ? 'blog' : 'podcast';
}

export default function ResourcesPage() {
  const { hash } = useLocation();
  const section = sectionFromHash(hash);

  const [episodes, setEpisodes] = useState<YouTubeEpisode[]>([]);
  const [episodesLoading, setEpisodesLoading] = useState(true);
  const [activeEpisode, setActiveEpisode] = useState<YouTubeEpisode | null>(null);
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [blogPosts, setBlogPosts] = useState<SbrBlogPostSummary[]>([]);
  const [blogLoading, setBlogLoading] = useState(true);
  const [canScrollBlogLeft, setCanScrollBlogLeft] = useState(false);
  const [canScrollBlogRight, setCanScrollBlogRight] = useState(false);
  const [selectedShow, setSelectedShow] = useState<PodcastShow | null>(null);
  const episodesTrackRef = useRef<HTMLDivElement>(null);
  const blogTrackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadEpisodes() {
      setEpisodesLoading(true);
      try {
        const latest = await fetchLatestYouTubeEpisodes(6);
        if (!cancelled) {
          setEpisodes(latest);
          setActiveEpisode(latest[0] ?? null);
          setShouldAutoplay(false);
        }
      } catch {
        if (!cancelled) {
          setEpisodes([]);
          setActiveEpisode(null);
          toast.error('Could not load episodes from YouTube. Try again later.');
        }
      } finally {
        if (!cancelled) setEpisodesLoading(false);
      }
    }

    loadEpisodes();
    return () => {
      cancelled = true;
    };
  }, []);

  function handleSelectShow(show: PodcastShow) {
    setSelectedShow((current) => (current?.id === show.id ? null : show));
  }

  function selectEpisode(ep: YouTubeEpisode) {
    setShouldAutoplay(true);
    setActiveEpisode(ep);
  }

  function updateEpisodeCarouselScroll() {
    const track = episodesTrackRef.current;
    if (!track) return;
    const { scrollLeft, scrollWidth, clientWidth } = track;
    const edge = 4;
    setCanScrollLeft(scrollLeft > edge);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - edge);
  }

  function scrollEpisodesBy(direction: 'prev' | 'next') {
    const track = episodesTrackRef.current;
    if (!track) return;
    const card = track.querySelector<HTMLElement>('.resources-episode-card');
    const step = card ? card.offsetWidth + 20 : 340;
    track.scrollBy({ left: direction === 'next' ? step : -step, behavior: 'smooth' });
  }

  useEffect(() => {
    const track = episodesTrackRef.current;
    if (!track || episodes.length === 0) return;

    updateEpisodeCarouselScroll();
    track.addEventListener('scroll', updateEpisodeCarouselScroll, { passive: true });
    const observer = new ResizeObserver(updateEpisodeCarouselScroll);
    observer.observe(track);

    return () => {
      track.removeEventListener('scroll', updateEpisodeCarouselScroll);
      observer.disconnect();
    };
  }, [episodes]);

  function updateBlogCarouselScroll() {
    const track = blogTrackRef.current;
    if (!track) return;
    const { scrollLeft, scrollWidth, clientWidth } = track;
    const edge = 4;
    setCanScrollBlogLeft(scrollLeft > edge);
    setCanScrollBlogRight(scrollLeft + clientWidth < scrollWidth - edge);
  }

  function scrollBlogBy(direction: 'prev' | 'next') {
    const track = blogTrackRef.current;
    if (!track) return;
    const card = track.querySelector<HTMLElement>('.resources-blog-card');
    const step = card ? card.offsetWidth + 24 : 360;
    track.scrollBy({ left: direction === 'next' ? step : -step, behavior: 'smooth' });
  }

  useEffect(() => {
    let cancelled = false;

    async function loadBlog() {
      setBlogLoading(true);
      try {
        const posts = await fetchLatestSbrBlogPosts(16);
        if (!cancelled) setBlogPosts(posts);
      } catch {
        if (!cancelled) {
          setBlogPosts([]);
          toast.error('Could not load blog posts.');
        }
      } finally {
        if (!cancelled) setBlogLoading(false);
      }
    }

    loadBlog();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const track = blogTrackRef.current;
    if (section !== 'blog' || !track || blogPosts.length === 0) return;

    const syncScroll = () => updateBlogCarouselScroll();

    syncScroll();
    const raf = requestAnimationFrame(syncScroll);
    track.addEventListener('scroll', syncScroll, { passive: true });
    const observer = new ResizeObserver(syncScroll);
    observer.observe(track);

    return () => {
      cancelAnimationFrame(raf);
      track.removeEventListener('scroll', syncScroll);
      observer.disconnect();
    };
  }, [blogPosts, section]);

  return (
    <>
      <section className="hero-pattern text-white resources-hero">
        <Container className="resources-hero__container">
          <Breadcrumb className="solution-hero-breadcrumb mb-3">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/resources#podcasts' }}>
              Resources
            </Breadcrumb.Item>
            <Breadcrumb.Item active>
              {section === 'blog' ? 'Blog' : 'Podcasts'}
            </Breadcrumb.Item>
          </Breadcrumb>

          <Row className="resources-hero__row gy-3">
            <Col lg={7} className="d-flex flex-column">
              <HeroAnimated>
                <h1 data-hero-item className="display-5 fw-bold mb-3">
                  Resources that <span className="text-gradient-teal-blue">move hiring</span> forward.
                </h1>
                <p data-hero-item className="lead text-white-50 mb-0 pe-lg-3">
                  Expert perspectives, real conversations, and practical strategies to help you hire smarter and lead with
                  confidence.
                </p>
              </HeroAnimated>
            </Col>
            <Col lg={5} className="resources-hero__media-col">
              <ResourcesHeroWaves />
            </Col>
          </Row>
        </Container>
      </section>

      <section className="resources-content bg-sbr-light py-5 py-lg-6">
        <RevealSection className="resources-content__reveal">
          <Container>
            {section === 'podcast' ? (
            <div className="resources-podcast">
                <PodcastLibrarySection
                  selectedShowId={selectedShow?.id ?? null}
                  onSelectShow={handleSelectShow}
                />

                {selectedShow ? (
                  <PodcastShowEpisodesSection
                    show={selectedShow}
                    onClose={() => setSelectedShow(null)}
                  />
                ) : null}

                <Row className="gy-4 gy-lg-5 align-items-stretch resources-podcast__featured-row">
                  <Col lg={5}>
                    <h2 className="resources-podcast__title sbr-section__title sbr-section__title--accent fw-bold mb-3">
                      Latest Episodes
                    </h2>
                    <p className="resources-podcast__lead mb-4">
                      Conversations with executives, operators, and thought leaders on hiring, leadership, and building
                      high-performing teams.
                    </p>
                    <Button
                      variant="secondary"
                      size="lg"
                      className="resources-podcast__cta"
                      href={YOUTUBE_CHANNEL_VIDEOS_URL}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Listen on YouTube Platform
                    </Button>
                  </Col>
                  <Col lg={7}>
                    {episodesLoading ? (
                      <div className="resources-podcast-featured resources-podcast-featured--loading">
                        <p className="mb-0 text-white-50">Loading latest episodes from YouTube…</p>
                      </div>
                    ) : activeEpisode ? (
                      <article className="resources-podcast-featured">
                        <div className="resources-podcast-featured__content">
                          <span className="resources-podcast-featured__badge">
                            {activeEpisode.id === episodes[0]?.id ? 'Latest Episode' : 'Now Playing'}
                          </span>
                          <h3 className="resources-podcast-featured__heading">{activeEpisode.title}</h3>
                          <p className="resources-podcast-featured__guest">
                            with{' '}
                            <span className="resources-podcast-featured__guest-name">{activeEpisode.guestName}</span> /{' '}
                            {activeEpisode.guestTitle}
                          </p>
                          <a
                            className="resources-podcast-featured__watch-link"
                            href={activeEpisode.watchUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Open on YouTube
                          </a>
                        </div>
                        <div className="resources-podcast-featured__player-wrap">
                          <ResourcesPodcastPlayer
                            videoId={activeEpisode.id}
                            title={activeEpisode.title}
                            thumbnailUrl={activeEpisode.thumbnailUrl}
                            autoplay={shouldAutoplay}
                          />
                        </div>
                      </article>
                    ) : (
                      <div className="resources-podcast-featured resources-podcast-featured--loading">
                        <p className="mb-0 text-white-50">No episodes available right now.</p>
                      </div>
                    )}
                  </Col>
                </Row>

                <div className="resources-podcast__carousel">
                  {episodes.length > 0 && (
                    <button
                      type="button"
                      className={[
                        'resources-podcast__carousel-btn',
                        'resources-podcast__carousel-btn--prev',
                        !canScrollLeft && 'resources-podcast__carousel-btn--hidden',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => scrollEpisodesBy('prev')}
                      disabled={!canScrollLeft}
                      aria-label="Scroll to previous episodes"
                    >
                      <SbrIcon icon={LuChevronLeft} size={22} />
                    </button>
                  )}
                  <div ref={episodesTrackRef} className="resources-podcast__episodes">
                    {episodes.map((ep) => {
                      const isActive = activeEpisode?.id === ep.id;
                      return (
                        <article
                          key={ep.id}
                          className={['resources-episode-card', isActive && 'resources-episode-card--active']
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <button
                            type="button"
                            className="resources-episode-card__select"
                            onClick={() => selectEpisode(ep)}
                            aria-label={`Play ${ep.title}`}
                            aria-pressed={isActive}
                          >
                            <div className="resources-episode-card__thumb">
                              <EpisodeThumbnail videoId={ep.id} />
                              <span className="resources-episode-card__play-overlay" aria-hidden>
                                <SbrIcon icon={LuPlay} size={20} />
                              </span>
                            </div>
                            <h4 className="resources-episode-card__title">{ep.title}</h4>
                          </button>
                        </article>
                      );
                    })}
                  </div>
                  {episodes.length > 0 && (
                    <button
                      type="button"
                      className={[
                        'resources-podcast__carousel-btn',
                        'resources-podcast__carousel-btn--next',
                        !canScrollRight && 'resources-podcast__carousel-btn--hidden',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => scrollEpisodesBy('next')}
                      disabled={!canScrollRight}
                      aria-label="Scroll to next episodes"
                    >
                      <SbrIcon icon={LuChevronRight} size={22} />
                    </button>
                  )}
                </div>

                <p className="resources-podcast__view-all-wrap">
                  <a
                    className="resources-podcast__view-all"
                    href={YOUTUBE_CHANNEL_VIDEOS_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>View All Episodes</span>
                    <SbrIcon icon={LuChevronRight} size={18} className="resources-podcast__view-all-icon" />
                  </a>
                </p>
            </div>
            ) : (
            <div className="resources-blog">
                <Row className="gy-4 gy-lg-5 align-items-start">
                  <Col lg={5} className="resources-blog__intro">
                    <p className="resources-blog__eyebrow">Blog</p>
                    <h2 className="resources-blog__title sbr-section__title sbr-section__title--accent fw-bold mb-3">Blog</h2>
                    <p className="resources-blog__lead mb-0">
                      Actionable advice, hiring trends, and talent strategies from our team and partners.
                    </p>
                  </Col>
                </Row>

                {blogLoading ? (
                  <p className="text-secondary mb-0 mt-4">Loading blog posts…</p>
                ) : blogPosts.length === 0 ? (
                  <p className="text-secondary mb-0 mt-4">No blog posts available right now.</p>
                ) : (
                  <div className="resources-blog__carousel">
                    <button
                      type="button"
                      className={[
                        'resources-blog__carousel-btn',
                        'resources-blog__carousel-btn--prev',
                        !canScrollBlogLeft && 'resources-blog__carousel-btn--hidden',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => scrollBlogBy('prev')}
                      disabled={!canScrollBlogLeft}
                      aria-label="Scroll to previous blog posts"
                    >
                      <SbrIcon icon={LuChevronLeft} size={22} />
                    </button>
                    <div ref={blogTrackRef} className="resources-blog__track">
                      {blogPosts.map((post) => (
                        <BlogPostCard key={post.id} post={post} titleAs="h3" />
                      ))}
                    </div>
                    <button
                      type="button"
                      className={[
                        'resources-blog__carousel-btn',
                        'resources-blog__carousel-btn--next',
                        !canScrollBlogRight && 'resources-blog__carousel-btn--hidden',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => scrollBlogBy('next')}
                      disabled={!canScrollBlogRight}
                      aria-label="Scroll to next blog posts"
                    >
                      <SbrIcon icon={LuChevronRight} size={22} />
                    </button>
                  </div>
                )}

                <p className="resources-podcast__view-all-wrap">
                  <Link to={SBR_BLOG_INDEX_PATH} className="resources-podcast__view-all">
                    <span>View All Blog Posts</span>
                    <SbrIcon icon={LuChevronRight} size={18} className="resources-podcast__view-all-icon" />
                  </Link>
                </p>
            </div>
            )}
          </Container>
        </RevealSection>
      </section>

      <GradientCTA
        icon={<i className="bi bi-calendar-event fs-4" aria-hidden />}
        title={
          <>
            <div>Want the playbook behind these resources?</div>
            <div className="sbr-cta__subtitle fw-normal opacity-75 mt-2">
              Schedule a working session with our team.
            </div>
          </>
        }
      />
    </>
  );
}
